import React, { useEffect, useState, useRef } from 'react';
import { validateVinChecksum } from '../utils/vinValidator';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * 차량-소유자 매칭 결과 패널
 * AUIGrid는 전역 객체(window.AUIGrid)로 사용
 */
const VehicleMatchingPanel = ({ fileList, apiBaseUrl, isRightPanelCollapsed, isMainLeftPanelCollapsed, setIsMainLeftPanelCollapsed, isVehicleRegistrationPanelCollapsed, setIsVehicleRegistrationPanelCollapsed }) => {
  const [matchedData, setMatchedData] = useState([]);
  const [unmatchedData, setUnmatchedData] = useState([]);
  const [allSupportDocs, setAllSupportDocs] = useState([]); // 모든 부가서류 (중복 차량 포함)
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [groupCode, setGroupCode] = useState('0001');
  const [userId, setUserId] = useState('jmkim');
  const [writeDate, setWriteDate] = useState(new Date().toISOString().split('T')[0]); // YYYY-MM-DD 형식
  const [isSaving, setIsSaving] = useState(false);

  // 수출화주 관련 상태
  const [expoCompanyNo, setExpoCompanyNo] = useState(563); // 수출화주 회사번호
  const [expoSangho, setExpoSangho] = useState('로그인네트웍'); // 수출화주 상호
  const [expoBusinessNumber, setExpoBusinessNumber] = useState(''); // 수출화주 사업자번호

  // 차주 정보 상태
  const [ownerName, setOwnerName] = useState(''); // 차주명
  const [ownerResidentNumber, setOwnerResidentNumber] = useState(''); // 차주 주민번호
  const [ownerAddress, setOwnerAddress] = useState(''); // 차주 주소

  // 환급요율, 요율 상태
  const [refundRate, setRefundRate] = useState(''); // 환급요율 (%)
  const [taxRate, setTaxRate] = useState(''); // 요율 (%)

  // 첨부파일 상태
  const [attachedFiles, setAttachedFiles] = useState([]); // 첨부파일 목록

  // 수출화주 검색 팝업 상태
  const [showCompanySearchModal, setShowCompanySearchModal] = useState(false);
  const [companySearchText, setCompanySearchText] = useState('');
  const [companyList, setCompanyList] = useState([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);

  // 저장 후 확인 상태
  const [savedVehicles, setSavedVehicles] = useState([]); // 저장된 차량 목록
  const [showSavedVehiclesModal, setShowSavedVehiclesModal] = useState(false);

  // 건별 수출화주 관리
  const [vehicleExpoCompanies, setVehicleExpoCompanies] = useState({}); // { vehicleIndex: { companyNo, sangho, businessNumber } }

  // 등록 모달 그리드 데이터
  const [registrationGridData, setRegistrationGridData] = useState([]);
  const registrationGridRef = useRef(null);

  // 건별 수출화주 검색 팝업
  const [showVehicleCompanySearch, setShowVehicleCompanySearch] = useState(false);
  const [vehicleCompanySearchIdx, setVehicleCompanySearchIdx] = useState(null); // 검색 대상 차량 인덱스 (null이면 전체)

  // 중복 체크 관련 상태
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [pendingVehicles, setPendingVehicles] = useState([]);
  const [allDuplicateResults, setAllDuplicateResults] = useState([]); // ⭐ 모든 중복 결과 저장

  const matchedGridRef = useRef(null);
  const unmatchedGridRef = useRef(null);

  // 매칭 API 호출 - 모든 파일의 모든 페이지 처리
  useEffect(() => {
    if (!fileList || fileList.length === 0) {
      return;
    }

    const fetchMatching = async () => {
      setLoading(true);
      setError(null);

      try {
        // 모든 파일의 모든 페이지를 하나의 배열로 합침 (uploadedFilePath, originalFileName 포함)
        const allPageResults = [];
        fileList.forEach(file => {
          if (file.pageResults && file.pageResults.length > 0) {
            // ⭐ 각 페이지에 파일의 uploadedFilePath와 originalFileName 추가
            const pagesWithPath = file.pageResults.map(page => ({
              ...page,
              uploadedFilePath: file.uploadedFilePath || file.path || '',
              originalFileName: file.serverOriginalFileName || file.name || '' // ⭐ 원본 파일명 추가
            }));
            allPageResults.push(...pagesWithPath);
          }
        });

        if (allPageResults.length === 0) {
          setLoading(false);
          return;
        }

        console.log(`🔗 전체 매칭 시작: ${fileList.length}개 파일, ${allPageResults.length}개 페이지`);

        const response = await fetch(`${apiBaseUrl}/api/ocr/match-vehicles`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pageResults: allPageResults })
        });

        if (!response.ok) {
          throw new Error(`API 오류: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          console.log('🔍 매칭 API 응답:', result);
          console.log('📊 매칭된 차량:', result.matched.length, '건');
          console.log('📊 미매칭 차량:', result.unmatched.length, '건');
          console.log('📊 부가서류:', result.allSupportDocs?.length || 0, '건');
          console.log('📊 요약:', result.summary);

          setMatchedData(result.matched);
          setUnmatchedData(result.unmatched);
          setAllSupportDocs(result.allSupportDocs || []); // 모든 부가서류
          setSummary(result.summary);

          console.log('📄 부가서류 전체:', result.allSupportDocs);
        } else {
          throw new Error(result.error || '매칭 실패');
        }
      } catch (err) {
        console.error('❌ 매칭 API 오류:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMatching();
  }, [fileList, apiBaseUrl]);

  // AUIGrid 컬럼 정의 (차량 문서 - 상단)
  const vehicleColumns = [
    { dataField: 'pageNumber', headerText: '페이지', width: 60, style: 'text-align:center;' },
    { dataField: 'documentType', headerText: '문서타입', width: 130 },
    { dataField: 'plateNumber', headerText: '차량번호', width: 100 },
    {
      dataField: 'vin', headerText: '차대번호', width: 160,
      renderer: {
        type: 'TemplateRenderer',
        baseClass: 'my_template',
        template: function (rowIndex, columnIndex, value, item) {
          if (!value || value.trim() === '') {
            return `<span>-</span>`;
          }
          // VIN 체크섬 검증 (전역 함수로 사용)
          try {
            const vinValidation = window.validateVinChecksum ? window.validateVinChecksum(value) : null;
            if (vinValidation && vinValidation.isValid) {
              return `<span title="${vinValidation.message}">${value} <span style="color: green; font-weight: bold;" title="체크섬 검증 통과">✓</span></span>`;
            } else if (vinValidation && !vinValidation.isValid) {
              return `<span title="${vinValidation.message}">${value} <span style="color: red; font-weight: bold;" title="체크섬 오류">✗</span></span>`;
            }
          } catch (e) {
            console.error('VIN 검증 오류:', e);
          }
          return `<span>${value}</span>`;
        }
      }
    },
    { dataField: 'model', headerText: '차명', width: 110 },
    { dataField: 'modelYear', headerText: '연식', width: 70, style: 'text-align:center;' },
    { dataField: 'ownerName', headerText: '소유자명', width: 100 },
    { dataField: 'mileage', headerText: '주행거리', width: 90, style: 'text-align:right;' },
    { dataField: 'displacement', headerText: '배기량', width: 80, style: 'text-align:right;' },
    { dataField: 'weight', headerText: '총중량', width: 80, style: 'text-align:right;' },
    { dataField: 'length', headerText: '길이', width: 70, style: 'text-align:right;' },
    { dataField: 'width', headerText: '너비', width: 70, style: 'text-align:right;' },
    { dataField: 'height', headerText: '높이', width: 70, style: 'text-align:right;' },
    { dataField: 'cbm', headerText: 'CBM', width: 80, style: 'text-align:right;' },
    { dataField: 'totalAmount', headerText: '총금액', width: 100, style: 'text-align:right;' },
    { dataField: 'tax', headerText: '세액', width: 100, style: 'text-align:right;' },
    { dataField: 'address', headerText: '사용본거지', width: 280 },
    {
      dataField: 'matchedIdentityPage', headerText: '매칭\n신분증', width: 70, style: 'text-align:center;',
      renderer: {
        type: 'TemplateRenderer',
        baseClass: 'my_template',
        template: function (rowIndex, columnIndex, value, item) {
          if (value) {
            return `<span style="font-weight:bold;">✓ P.${value}</span>`;
          }
          return `<span>-</span>`;
        }
      }
    },
    {
      dataField: 'matchedBusinessPage', headerText: '매칭\n사업자', width: 70, style: 'text-align:center;',
      renderer: {
        type: 'TemplateRenderer',
        baseClass: 'my_template',
        template: function (rowIndex, columnIndex, value, item) {
          if (value) {
            return `<span style="font-weight:bold;">✓ P.${value}</span>`;
          }
          return `<span>-</span>`;
        }
      }
    }
  ];

  // AUIGrid 컬럼 정의 (부가 서류 - 하단)
  const supportColumns = [
    { dataField: 'pageNumber', headerText: '페이지', width: 60, style: 'text-align:center;' },
    { dataField: 'documentType', headerText: '문서타입', width: 130 },
    { dataField: 'name', headerText: '이름/상호', width: 100 },
    { dataField: 'residentNumber', headerText: '주민번호', width: 130 },
    { dataField: 'businessNumber', headerText: '사업자번호', width: 130 },
    { dataField: 'plateNumber', headerText: '차량번호', width: 100 }, // 중복 차량 문서용
    { dataField: 'vin', headerText: '차대번호', width: 160 }, // 중복 차량 문서용
    { dataField: 'address', headerText: '주소', width: 280 },
    {
      dataField: 'matchedVehiclePages', headerText: '매칭된\n차량', width: 150,
      renderer: {
        type: 'TemplateRenderer',
        baseClass: 'my_template',
        template: function (rowIndex, columnIndex, value, item) {
          if (value && value.length > 0) {
            const pages = value.map(p => `P.${p}`).join(', ');
            return `<span style="color:#10B981;font-weight:bold;">✓ ${pages}</span>`;
          }
          return `<span style="color:#D1D5DB;">-</span>`;
        }
      }
    }
  ];

  // 차량 문서 및 부가 서류 데이터 재구성
  const [vehicleData, setVehicleData] = useState([]);
  const [supportData, setSupportData] = useState([]);

  useEffect(() => {
    if (!matchedData && !unmatchedData && !allSupportDocs) return;

    // 차량 문서 데이터 (매칭 + 미매칭 통합)
    const vehicles = [];

    // 매칭된 차량 추가
    matchedData.forEach(item => {
      vehicles.push({
        pageNumber: item.vehiclePageNumber,
        documentType: item.documentType,
        plateNumber: item.plateNumber || '',
        vin: item.vin || '',
        model: item.model || '',
        modelYear: item.modelYear || '',
        ownerName: item.ownerName || '',
        residentNumber: item.residentNumber || item.jumin || item.ucaRitMcarJumin || '', // 주민번호 매핑 추가
        mileage: item.mileage || '',
        displacement: item.displacement || '',
        weight: item.weight || item.totalWeight || '',
        length: item.length || '',
        width: item.width || item.deep || '',
        height: item.height || '',
        cbm: item.cbm || '',
        totalAmount: item.totalAmount || item.saleAmount || '',
        tax: item.tax || item.refundCost || '',
        address: item.address || '',
        matchedIdentityPage: item.identityPageNumber || null,
        matchedBusinessPage: null // TODO: 사업자 매칭 추가
      });
    });

    // 미매칭 차량 추가
    unmatchedData.forEach(item => {
      vehicles.push({
        pageNumber: item.vehiclePageNumber,
        documentType: item.documentType,
        plateNumber: item.plateNumber || '',
        vin: item.vin || '',
        model: item.model || '',
        modelYear: item.modelYear || '',
        ownerName: item.ownerName || '',
        residentNumber: item.residentNumber || item.jumin || item.ucaRitMcarJumin || '', // 주민번호 매핑 추가
        mileage: item.mileage || '',
        displacement: item.displacement || '',
        weight: item.weight || item.totalWeight || '',
        length: item.length || '',
        width: item.width || item.deep || '',
        height: item.height || '',
        cbm: item.cbm || '',
        totalAmount: item.totalAmount || item.saleAmount || '',
        tax: item.tax || item.refundCost || '',
        address: item.address || '',
        matchedIdentityPage: null,
        matchedBusinessPage: null
      });
    });

    // 부가서류 처리 (allSupportDocs에서 직접 사용)
    const vehiclePageNumbers = new Set([...matchedData, ...unmatchedData].map(v => v.vehiclePageNumber));
    const supports = allSupportDocs.map(doc => {
      const data = doc.data || {};
      return {
        pageNumber: doc.pageNumber,
        documentType: doc.documentType,
        name: data.name || data.businessName || data.ownerName || '',
        residentNumber: data.residentNumber || '',
        businessNumber: data.businessNumber || '',
        plateNumber: data.plateNumber || '', // 중복 차량 문서용
        vin: data.vin || '', // 중복 차량 문서용
        address: data.address || '',
        matchedVehiclePages: [] // TODO: 매칭 관계 역산
      };
    });

    setVehicleData(vehicles.sort((a, b) => a.pageNumber - b.pageNumber));
    setSupportData(supports.sort((a, b) => a.pageNumber - b.pageNumber));
  }, [matchedData, unmatchedData, allSupportDocs]);

  // VIN 체크섬 검증 함수를 전역으로 등록 (AUIGrid TemplateRenderer에서 사용)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.validateVinChecksum = validateVinChecksum;
    }
    return () => {
      if (typeof window !== 'undefined' && window.validateVinChecksum) {
        delete window.validateVinChecksum;
      }
    };
  }, []);

  // 차량 문서 그리드 생성
  useEffect(() => {
    if (matchedGridRef.current && vehicleData.length > 0) {
      // 기존 그리드 제거
      if (window.AUIGrid && window.AUIGrid.isCreated('#vehicle-grid')) {
        window.AUIGrid.destroy('#vehicle-grid');
      }

      const gridProps = {
        headerHeight: 32,
        rowHeight: 30,
        showRowNumColumn: true,
        rowNumHeaderText: '번호',
        enableColumnResize: true,

        enableSorting: true,
        enableFilter: true,
        enableCellMerge: false,
        showRowCheckColumn: true,
        enableRowCheckShiftKey: true,
        independentAllCheckBox: false
      };

      // 그리드 생성
      window.AUIGrid.create('#vehicle-grid', vehicleColumns, gridProps);
      window.AUIGrid.setGridData('#vehicle-grid', vehicleData);

      console.log('✅ 차량 그리드 생성 완료:', vehicleData.length, '건');
    }
  }, [vehicleData]);

  // 부가 서류 그리드 생성
  useEffect(() => {
    if (unmatchedGridRef.current && supportData.length > 0) {
      // 기존 그리드 제거
      if (window.AUIGrid && window.AUIGrid.isCreated('#support-grid')) {
        window.AUIGrid.destroy('#support-grid');
      }

      const gridProps = {
        headerHeight: 32,
        rowHeight: 30,
        showRowNumColumn: true,
        rowNumHeaderText: '번호',
        enableColumnResize: true,

        enableSorting: true,
        enableFilter: true
      };

      window.AUIGrid.create('#support-grid', supportColumns, gridProps);
      window.AUIGrid.setGridData('#support-grid', supportData);
    }
  }, [supportData]);

  // 오른쪽 패널 접기/펼치기에 따른 AUIGrid 리사이즈
  useEffect(() => {
    if (!window.AUIGrid) return;

    // 짧은 지연 후 리사이즈 (DOM 업데이트 대기)
    const timer = setTimeout(() => {
      ['vehicle-grid', 'support-grid'].forEach(gridId => {
        try {
          if (window.AUIGrid.isCreated(`#${gridId}`)) {
            window.AUIGrid.resize(`#${gridId}`);
            console.log(`✅ AUIGrid 리사이즈 완료: ${gridId}`);
          }
        } catch (e) {
          console.warn(`그리드 리사이즈 실패 (${gridId}):`, e);
        }
      });
    }, 300); // 300ms 지연

    return () => clearTimeout(timer);
  }, [isRightPanelCollapsed]);

  // 중복 모달 그리드 초기화
  useEffect(() => {
    if (!window.AUIGrid || !showDuplicateModal || !allDuplicateResults || allDuplicateResults.length === 0) {
      return;
    }

    // 상단 그리드: 업로드한 차량 리스트
    const uploadGridColumns = [
      { dataField: 'no', headerText: '번호', width: 60, style: 'text-align:center;' },
      { dataField: 'plateNumber', headerText: '차량번호', width: 120 },
      { dataField: 'vin', headerText: '차대번호', width: 180 },
      { dataField: 'model', headerText: '차명', width: 120 },
      { dataField: 'modelYear', headerText: '연식', width: 80, style: 'text-align:center;' },
      { dataField: 'ownerName', headerText: '소유자명', width: 150 },
      { dataField: 'mileage', headerText: '주행거리', width: 100, style: 'text-align:right;' },
      {
        dataField: 'duplicateCount', headerText: '중복건수', width: 100, style: 'text-align:center;',
        renderer: {
          type: 'TemplateRenderer',
          callback: (rowIndex, columnIndex, value, headerText, item, dataField) => {
            const count = item.duplicateCount || 0;
            return count > 0 ? `<span style="color: red; font-weight: bold;">${count}건</span>` : '<span style="color: green;">없음</span>';
          }
        }
      }
    ];

    const uploadGridData = allDuplicateResults.map((result, index) => ({
      no: index + 1,
      plateNumber: result.vehicle.plateNumber,
      vin: result.vehicle.vin,
      model: result.vehicle.model,
      modelYear: result.vehicle.modelYear,
      ownerName: result.vehicle.ownerName,
      mileage: result.vehicle.mileage ? parseInt(String(result.vehicle.mileage).replace(/[^0-9]/g, '')).toLocaleString() : '-',
      duplicateCount: result.duplicates.length,
      ...result.vehicle
    }));

    // 하단 그리드: 중복건 목록
    const duplicateGridColumns = [
      { dataField: 'sourcePlateNumber', headerText: '원본 차량번호', width: 120 },
      { dataField: 'sourceVin', headerText: '원본 차대번호', width: 180 },
      { dataField: 'ucarItmNo', headerText: 'Key', width: 100, style: 'text-align:center;' },
      { dataField: 'plateNumber', headerText: '차량번호', width: 120 },
      { dataField: 'vin', headerText: '차대번호', width: 180 },
      { dataField: 'carName', headerText: '차명', width: 120 },
      { dataField: 'year', headerText: '연식', width: 80, style: 'text-align:center;' },
      {
        dataField: 'mileage', headerText: '주행거리', width: 100, style: 'text-align:right;',
        renderer: {
          type: 'TemplateRenderer',
          callback: (rowIndex, columnIndex, value, headerText, item, dataField) => {
            return value ? `${value.toLocaleString()}km` : '-';
          }
        }
      },
      {
        dataField: 'duplicateType', headerText: '중복타입', width: 120, style: 'text-align:center;',
        renderer: {
          type: 'TemplateRenderer',
          callback: (rowIndex, columnIndex, value, headerText, item, dataField) => {
            const colors = {
              'FULL': 'red',
              'PLATE_ONLY': 'orange',
              'VIN_ONLY': 'yellow'
            };
            const labels = {
              'FULL': '완전 중복',
              'PLATE_ONLY': '차량번호 중복',
              'VIN_ONLY': '차대번호 중복'
            };
            return `<span style="color: ${colors[value] || 'gray'}; font-weight: bold;">${labels[value] || value}</span>`;
          }
        }
      },
      {
        dataField: 'regDate', headerText: '등록일', width: 120, style: 'text-align:center;',
        renderer: {
          type: 'TemplateRenderer',
          callback: (rowIndex, columnIndex, value, headerText, item, dataField) => {
            return value ? new Date(value).toLocaleDateString('ko-KR') : '-';
          }
        }
      },
      { dataField: 'regId', headerText: '등록자', width: 100 }
    ];

    const duplicateGridData = [];
    allDuplicateResults.forEach(result => {
      result.duplicates.forEach(dup => {
        duplicateGridData.push({
          sourcePlateNumber: result.vehicle.plateNumber,
          sourceVin: result.vehicle.vin,
          ucarItmNo: dup.ucarItmNo,
          plateNumber: dup.plateNumber,
          vin: dup.vin,
          carName: dup.carName,
          year: dup.year,
          mileage: dup.mileage,
          duplicateType: dup.duplicateType,
          regDate: dup.regDate,
          regId: dup.regId,
          ...dup
        });
      });
    });

    // 상단 그리드 생성
    const timer1 = setTimeout(() => {
      try {
        if (window.AUIGrid.isCreated('#upload-vehicle-grid')) {
          window.AUIGrid.destroy('#upload-vehicle-grid');
        }
        window.AUIGrid.create('#upload-vehicle-grid', uploadGridColumns, {
          headerHeight: 32,
          rowHeight: 30,
          showRowNumColumn: true,
          rowNumHeaderText: '번호',
          enableColumnResize: true,
  
          enableSorting: true,
          enableFilter: true
        });
        window.AUIGrid.setGridData('#upload-vehicle-grid', uploadGridData);
      } catch (e) {
        console.error('상단 그리드 생성 오류:', e);
      }
    }, 100);

    // 하단 그리드 생성
    const timer2 = setTimeout(() => {
      try {
        if (window.AUIGrid.isCreated('#duplicate-vehicle-grid')) {
          window.AUIGrid.destroy('#duplicate-vehicle-grid');
        }
        window.AUIGrid.create('#duplicate-vehicle-grid', duplicateGridColumns, {
          headerHeight: 32,
          rowHeight: 30,
          showRowNumColumn: true,
          rowNumHeaderText: '번호',
          enableColumnResize: true,
  
          enableSorting: true,
          enableFilter: true
        });
        window.AUIGrid.setGridData('#duplicate-vehicle-grid', duplicateGridData);
      } catch (e) {
        console.error('하단 그리드 생성 오류:', e);
      }
    }, 200);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      if (window.AUIGrid) {
        try {
          if (window.AUIGrid.isCreated('#upload-vehicle-grid')) {
            window.AUIGrid.destroy('#upload-vehicle-grid');
          }
          if (window.AUIGrid.isCreated('#duplicate-vehicle-grid')) {
            window.AUIGrid.destroy('#duplicate-vehicle-grid');
          }
        } catch (e) {
          console.warn('그리드 정리 오류:', e);
        }
      }
    };
  }, [showDuplicateModal, allDuplicateResults]);

  // 등록 모달 AUIGrid 초기화 - registrationGridData 변경 시마다 그리드 재생성
  useEffect(() => {
    if (!window.AUIGrid || !showRegistrationModal || registrationGridData.length === 0) {
      return;
    }

    const timer = setTimeout(() => {
      const gridContainer = document.getElementById('registration-grid');
      if (!gridContainer) return;

      // ⭐ 항상 그리드 재생성 (렌더러 재실행을 위해)
      if (window.AUIGrid.isCreated('#registration-grid')) {
        console.log('🔄 그리드 재생성 (렌더러 재실행)');
        window.AUIGrid.destroy('#registration-grid');
      }

      // 그리드 컬럼 정의
      const columnLayout = [
        {
          dataField: '_checked',
          headerText: '',
          width: 40,
          editable: false,
          headerRenderer: {
            type: 'CheckBoxHeaderRenderer',
            dependentMode: true
          },
          renderer: {
            type: 'CheckBoxEditRenderer'
          }
        },
        {
          dataField: 'documentType', headerText: '문서타입', width: 80, editable: false,
          renderer: {
            type: 'TemplateRenderer',
            callback: (rowIndex, columnIndex, value) => {
              const colors = {
                '말소등록증': 'bg-red-100 text-red-700',
                '자동차등록증': 'bg-blue-100 text-blue-700',
                '경락확인서': 'bg-gray-100 text-yellow-700'
              };
              const colorClass = colors[value] || 'bg-gray-100 text-gray-700';
              return `<span class="px-1.5 py-0.5 rounded text-xs ${colorClass}">${value || '-'}</span>`;
            }
          }
        },
        {
          dataField: 'expoSangho',
          headerText: '수출화주',
          width: 130,
          editable: false,
          style: 'cursor:pointer;',
          renderer: {
            type: 'TemplateRenderer',
            callback: (rowIndex, columnIndex, value, headerText, item) => {
              return `<div class="expo-cell flex items-center justify-between hover:bg-blue-50 px-1 rounded" data-idx="${item._idx}" style="cursor:pointer;">
                <span class="truncate">${value || '선택'}</span>
                <span style="color:#3b82f6;">🔍</span>
              </div>`;
            }
          }
        },
        { dataField: 'plateNumber', headerText: '차량번호', width: 90, editable: true },
        { dataField: 'vin', headerText: '차대번호', width: 160, editable: true },
        { dataField: 'model', headerText: '차량명', width: 80, editable: true },
        { dataField: 'modelYear', headerText: '연식', width: 50, editable: true, style: 'text-align:center;' },
        { dataField: 'ownerName', headerText: '차주명', width: 70, editable: true },
        { dataField: 'residentNumber', headerText: '주민번호', width: 110, editable: true },
        {
          dataField: 'displayFileName', // ⭐ 렌더러 대신 데이터 필드 직접 사용
          headerText: '첨부파일',
          width: 200,
          editable: false,
          style: 'color:#2563eb; text-decoration:underline; cursor:pointer;',
          prefix: '📎 ' // ⭐ 앞에 아이콘 추가
        },
        { dataField: 'address', headerText: '주소', width: 250, editable: true }
      ];

      // 그리드 속성
      const gridProps = {
        width: '100%',
        height: '100%',
        rowHeight: 28,
        headerHeight: 32,
        showRowNumColumn: true,
        rowNumHeaderText: 'No',
        editable: true,
        selectionMode: 'multipleCells',
        showStateColumn: false, // 상태 컬럼 숨김
        enableFilter: false,
        enableSorting: true,
        enableColumnResize: true,

        softRemoveRowMode: false,
        useContextMenu: true,
        enableMovingColumn: true,
        showFooter: false
      };

      // 그리드 생성
      window.AUIGrid.create('#registration-grid', columnLayout, gridProps);
      window.AUIGrid.setGridData('#registration-grid', registrationGridData);

      // 수출화주 셀 클릭 이벤트 - 셀 클릭 시 바로 검색 팝업 열기
      window.AUIGrid.bind('#registration-grid', 'cellClick', (event) => {
        if (event.dataField === 'expoSangho') {
          // 클릭한 행의 인덱스 가져오기
          const rowIndex = event.rowIndex;
          const item = event.item;
          setVehicleCompanySearchIdx(rowIndex); // ⭐ rowIndex 사용 (배열 인덱스와 일치)
          setShowVehicleCompanySearch(true);
          setCompanySearchText('');
          loadCompanyList('');
        }
      });

      // ⭐ 첨부파일 더블클릭 이벤트 - PDF 뷰잉
      window.AUIGrid.bind('#registration-grid', 'cellDoubleClick', (event) => {
        if (event.dataField === 'originalFileName') {
          const item = event.item;
          const pdfPath = item.pdfFilePath;
          const pageNum = item.pageNumber || 1;

          if (pdfPath) {
            // PDF 뷰어 열기 (새 창)
            const viewerUrl = `${apiBaseUrl}/api/pdf/view?path=${encodeURIComponent(pdfPath)}&page=${pageNum}`;
            window.open(viewerUrl, '_blank', 'width=800,height=1000,scrollbars=yes,resizable=yes');
            console.log(`📄 PDF 뷰어 열기: ${pdfPath}, 페이지 ${pageNum}`);
          } else {
            alert('PDF 파일 경로가 없습니다.');
          }
        }
      });

    }, 100);

    return () => {
      clearTimeout(timer);
      if (window.AUIGrid && window.AUIGrid.isCreated('#registration-grid')) {
        try {
          window.AUIGrid.destroy('#registration-grid');
        } catch (e) {
          console.warn('등록 그리드 정리 오류:', e);
        }
      }
    };
  }, [showRegistrationModal, registrationGridData]);

  // 엑셀 다운로드 (차량 문서)
  const handleExportVehicles = () => {
    if (window.AUIGrid && window.AUIGrid.isCreated('#vehicle-grid')) {
      window.AUIGrid.exportToXlsx('#vehicle-grid', {
        fileName: `차량문서_${new Date().toISOString().slice(0, 10)}.xlsx`,
        sheetName: '차량문서'
      });
    }
  };

  // 엑셀 다운로드 (부가 서류)
  const handleExportSupports = () => {
    if (window.AUIGrid && window.AUIGrid.isCreated('#support-grid')) {
      window.AUIGrid.exportToXlsx('#support-grid', {
        fileName: `부가서류_${new Date().toISOString().slice(0, 10)}.xlsx`,
        sheetName: '부가서류'
      });
    }
  };

  // 차량 등록 버튼 클릭
  const handleRegisterVehiclesClick = () => {
    if (!window.AUIGrid || !window.AUIGrid.isCreated('#vehicle-grid')) {
      alert('그리드가 초기화되지 않았습니다.');
      return;
    }

    const checkedItems = window.AUIGrid.getCheckedRowItems('#vehicle-grid');
    console.log('🔍 체크된 항목:', checkedItems);
    console.log('🔍 체크된 항목 개수:', checkedItems ? checkedItems.length : 0);

    if (!checkedItems || checkedItems.length === 0) {
      alert('등록할 차량을 선택해주세요.');
      return;
    }

    console.log('✅ 차량 등록 모달 표시');
    // 수출화주 목록 로드 (모달 열릴 때)
    loadCompanyList('');

    // 등록 그리드 데이터 준비
    console.log('🔍 fileList 구조:', fileList?.map(f => ({
      name: f.name,
      serverOriginalFileName: f.serverOriginalFileName,
      uploadedFilePath: f.uploadedFilePath,
      pageResultsCount: f.pageResults?.length
    })));

    const gridData = checkedItems.map((item, idx) => {
      const v = item.item;
      const pageNum = v.vehiclePageNumber || v.pageNumber || 1;

      console.log(`🔍 [${idx}] 차량 데이터:`, {
        plateNumber: v.plateNumber,
        pageNumber: v.pageNumber,
        vehiclePageNumber: v.vehiclePageNumber,
        originalFileName: v.originalFileName,
        pdfFilePath: v.pdfFilePath,
        uploadedFilePath: v.uploadedFilePath
      });

      // ⭐ 우선순위: v.originalFileName > v.pdfFilePath > fileList에서 찾기
      let fileName = v.originalFileName || '';
      let filePath = v.pdfFilePath || v.uploadedFilePath || '';

      // fileList에서 파일명 찾기 (fileName이나 filePath가 없을 때)
      if ((!fileName || !filePath) && fileList && fileList.length > 0) {
        // 1. 먼저 pageResults에서 pageNumber로 찾기
        for (const file of fileList) {
          if (file.pageResults && file.pageResults.length > 0) {
            const matchingPage = file.pageResults.find(p => p.pageNumber === pageNum);
            if (matchingPage) {
              if (!fileName) {
                fileName = file.serverOriginalFileName || file.name || '';
              }
              if (!filePath) {
                filePath = file.uploadedFilePath || file.path || '';
              }
              console.log(`📎 [${idx}] pageResults에서 찾음: 페이지 ${pageNum} → ${fileName}`);
              break;
            }
          }
        }

        // 2. 못 찾으면 첫 번째 파일 사용 (단일 파일인 경우)
        if (!fileName && fileList.length === 1) {
          const file = fileList[0];
          fileName = file.serverOriginalFileName || file.name || '';
          filePath = file.uploadedFilePath || file.path || '';
          console.log(`📎 [${idx}] 단일 파일 사용: ${fileName}`);
        }
      }

      // 여전히 파일명이 없으면 경로에서 추출
      if (!fileName && filePath) {
        const pathParts = filePath.replace(/\\/g, '/').split('/');
        fileName = pathParts[pathParts.length - 1] || '';
        console.log(`📎 [${idx}] 경로에서 추출: ${fileName}`);
      }

      // ⭐ 한글 파일명 디코딩 (URL 인코딩된 경우)
      if (fileName) {
        try {
          // URL 인코딩된 경우 디코딩
          if (fileName.includes('%')) {
            fileName = decodeURIComponent(fileName);
          }
          // Buffer로 인코딩된 경우 (latin1 → utf8 변환)
          if (/[\x80-\xff]/.test(fileName)) {
            // latin1로 잘못 해석된 UTF-8 바이트를 복원
            const bytes = [];
            for (let i = 0; i < fileName.length; i++) {
              bytes.push(fileName.charCodeAt(i));
            }
            fileName = new TextDecoder('utf-8').decode(new Uint8Array(bytes));
          }
        } catch (e) {
          console.warn(`⚠️ 파일명 디코딩 실패: ${e.message}`);
        }
      }

      // ⭐ 수출화주 기본값 확인
      const defaultExpoSangho = expoSangho || '로그인네트웍';
      const plateNum = v.plateNumber || '';

      // ⭐ 표시용 첨부파일명 생성 (렌더러 대신 데이터에 직접 저장)
      let displayFileName = '';
      if (defaultExpoSangho && plateNum) {
        const expoName = defaultExpoSangho.substring(0, 10);
        displayFileName = `${expoName}_${plateNum}.pdf`;
      } else {
        const baseName = (fileName || '').replace(/\.[^/.]+$/, '');
        displayFileName = `${baseName}_${pageNum}.pdf`;
      }

      console.log(`📋 [${idx}] 최종: 차량=${plateNum}, 파일=${fileName}, 경로=${filePath}, 페이지=${pageNum}, 수출화주=${defaultExpoSangho}, 표시파일명=${displayFileName}`);

      return {
        _idx: idx,
        documentType: v.documentType || '',
        expoCompanyNo: expoCompanyNo || 563,
        expoSangho: defaultExpoSangho, // ⭐ 반드시 값이 있도록 보장
        expoBusinessNumber: expoBusinessNumber || '',
        plateNumber: plateNum,
        displayFileName: displayFileName, // ⭐ 표시용 파일명 추가
        vin: v.vin || '',
        model: v.model || '',
        modelYear: v.modelYear || '',
        ownerName: v.ownerName || ownerName || '',
        residentNumber: v.residentNumber || ownerResidentNumber || '',
        address: v.address || ownerAddress || '',
        originalFileName: fileName,
        pageNumber: pageNum,
        pdfFilePath: filePath,
        mileage: v.mileage || '',
        displacement: v.displacement || '',
        // 원본 데이터 보관
        _originalItem: v
      };
    });

    console.log('📊 등록 그리드 데이터:', gridData);
    setRegistrationGridData(gridData);

    // 건별 수출화주 초기화 (기본값으로)
    const initialExpoCompanies = {};
    checkedItems.forEach((_, idx) => {
      initialExpoCompanies[idx] = {
        companyNo: expoCompanyNo,
        sangho: expoSangho,
        businessNumber: expoBusinessNumber
      };
    });
    setVehicleExpoCompanies(initialExpoCompanies);
    setShowRegistrationModal(true);
  };

  // 중복 체크 수행
  const checkDuplicates = async (vehicles) => {
    const duplicateResults = [];

    for (const vehicle of vehicles) {
      try {
        const response = await fetch(`${apiBaseUrl}/api/vehicle/check-duplicate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            plateNumber: vehicle.plateNumber,
            vin: vehicle.vin,
            groupId: groupCode
          })
        });

        const result = await response.json();

        if (result.success && result.hasDuplicate) {
          duplicateResults.push({
            vehicle,
            duplicates: result.duplicates
          });
        }
      } catch (error) {
        console.error(`❌ 중복 체크 오류 (${vehicle.plateNumber}):`, error);
      }
    }

    return duplicateResults;
  };

  // 차량 등록 실행
  const handleRegisterVehicles = async () => {
    if (!groupCode || groupCode.trim() === '') {
      alert('그룹코드를 입력해주세요.');
      return;
    }

    if (!userId || userId.trim() === '') {
      alert('아이디를 입력해주세요.');
      return;
    }

    // ⭐ 등록 그리드에서 데이터 가져오기 (AUIGrid)
    let gridData = [];
    if (window.AUIGrid && window.AUIGrid.isCreated('#registration-grid')) {
      gridData = window.AUIGrid.getGridData('#registration-grid');
    } else {
      gridData = registrationGridData;
    }

    console.log('🚀 차량 등록 시작 - 그리드 데이터:', gridData);

    if (!gridData || gridData.length === 0) {
      alert('등록할 차량이 없습니다.');
      return;
    }

    // checkedItems 형식으로 변환 (기존 로직 호환)
    const checkedItems = gridData.map((item, idx) => ({
      item: {
        ...item._originalItem,
        // 그리드에서 수정된 값들 적용
        model: item.model,
        plateNumber: item.plateNumber,
        vin: item.vin,
        modelYear: item.modelYear,
        ownerName: item.ownerName,
        residentNumber: item.residentNumber,
        address: item.address,
        // 수출화주 정보
        expoCompanyNo: item.expoCompanyNo,
        expoSangho: item.expoSangho,
        expoBusinessNumber: item.expoBusinessNumber,
        // 파일 정보
        originalFileName: item.originalFileName,
        pageNumber: item.pageNumber,
        pdfFilePath: item.pdfFilePath
      },
      rowIndex: idx
    }));

    setShowRegistrationModal(false);

    // 중복 체크 수행
    const vehicles = checkedItems.map(item => item.item);
    console.log('🔍 중복 체크 시작...');
    const duplicateResults = await checkDuplicates(vehicles);

    if (duplicateResults.length > 0) {
      // 중복 발견 시 확인 팝업 표시
      console.log(`⚠️  중복 차량 발견: ${duplicateResults.length}건`);
      setAllDuplicateResults(duplicateResults); // ⭐ 모든 중복 결과 저장
      setDuplicateInfo(duplicateResults[0]); // 첫 번째 중복 차량 (기존 호환성)
      setPendingVehicles(checkedItems);
      setShowDuplicateModal(true);
      return;
    }

    // 중복 없으면 바로 등록
    await processVehicleRegistrationFromGrid(gridData, false, null);
  };

  // 전체 중복 차량 등록 처리 (각 차량별 첫 번째 중복 ID 사용)
  const processVehicleRegistrationForAllDuplicates = async (checkedItems, forceInsert = false) => {
    setIsSaving(true);
    setShowDuplicateModal(false);

    try {
      let successCount = 0;
      let failCount = 0;

      // 각 차량별로 중복 결과에서 첫 번째 중복 ID 찾기
      for (let idx = 0; idx < checkedItems.length; idx++) {
        const checkedItem = checkedItems[idx];
        const vehicle = checkedItem.item;
        // ⭐ 건별 수출화주 정보 가져오기
        const vehicleExpo = vehicleExpoCompanies[idx] || {
          companyNo: expoCompanyNo,
          sangho: expoSangho,
          businessNumber: expoBusinessNumber
        };

        const duplicateResult = allDuplicateResults.find(
          result => result.vehicle.plateNumber === vehicle.plateNumber &&
            result.vehicle.vin === vehicle.vin
        );

        const overwriteId = duplicateResult && duplicateResult.duplicates.length > 0
          ? duplicateResult.duplicates[0].ucarItmNo
          : null;

        try {
          const vehicleData = {
            '차명': vehicle.model,
            '차량등록번호': vehicle.plateNumber,
            '차대번호': vehicle.vin,
            '소유자명': vehicle.ownerName,
            '연식': vehicle.modelYear,
            '주행거리': vehicle.mileage,
            '배기량': vehicle.displacement,
            '사용본거지': vehicle.address,
            '문서타입': vehicle.documentType
          };

          const response = await fetch(`${apiBaseUrl}/api/vehicle/save`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              vehicleData,
              userId,
              groupId: groupCode,
              writeDate: writeDate,
              documentType: vehicle.documentType,
              pdfFilePath: vehicle.pdfFilePath || '',
              ocrPrompt: '차량등록매핑을 통한 일괄 등록',
              ocrRawResult: vehicle,
              forceInsert,
              overwriteId,
              // ⭐ 건별 수출화주 정보 추가
              expoCompanyNo: vehicleExpo.companyNo,
              expoSangho: vehicleExpo.sangho,
              expoBusinessNumber: vehicleExpo.businessNumber
            }),
          });

          const result = await response.json();

          if (response.ok) {
            successCount++;
            console.log(`✅ 차량 ${vehicle.plateNumber} 등록 성공`);
          } else {
            failCount++;
            console.error(`❌ 차량 ${vehicle.plateNumber} 등록 실패:`, result);
          }
        } catch (error) {
          failCount++;
          console.error(`❌ 차량 ${vehicle.plateNumber} 등록 오류:`, error);
        }
      }

      alert(`차량 등록 완료!\n성공: ${successCount}건\n실패: ${failCount}건`);
      setAllDuplicateResults([]);

    } catch (error) {
      console.error('❌ 차량 등록 오류:', error);
      alert('차량 등록 중 오류가 발생했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  // ⭐ 그리드 데이터로 차량 등록 처리 (새 함수)
  const processVehicleRegistrationFromGrid = async (gridData, forceInsert = false, overwriteId = null) => {
    setIsSaving(true);

    try {
      let successCount = 0;
      let failCount = 0;
      const savedList = [];

      for (const item of gridData) {
        try {
          const vehicleData = {
            '차명': item.model,
            '차량등록번호': item.plateNumber,
            '차대번호': item.vin,
            '소유자명': item.ownerName || '',
            '주민번호': item.residentNumber || '',
            '주소': item.address || '',
            '연식': item.modelYear,
            '주행거리': item.mileage || '',
            '배기량': item.displacement || '',
            '사용본거지': item.address || '',
            '환급요율': refundRate || '',
            '요율': taxRate || '',
            '문서타입': item.documentType
          };

          console.log('📤 API 요청:', {
            vehicleData,
            userId,
            groupId: groupCode,
            documentType: item.documentType,
            pdfFilePath: item.pdfFilePath,
            originalFileName: item.originalFileName,
            pageNumber: item.pageNumber,
            expoCompanyNo: item.expoCompanyNo,
            expoSangho: item.expoSangho
          });

          const response = await fetch(`${apiBaseUrl}/api/vehicle/save`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              vehicleData,
              userId,
              groupId: groupCode,
              writeDate: writeDate,
              documentType: item.documentType,
              pdfFilePath: item.pdfFilePath || '',
              originalFileName: item.originalFileName || '',
              pageNumber: item.pageNumber || 1,
              ocrPrompt: '차량등록매핑을 통한 일괄 등록',
              ocrRawResult: item._originalItem || item,
              forceInsert,
              overwriteId,
              // 수출화주 정보
              expoCompanyNo: item.expoCompanyNo,
              expoSangho: item.expoSangho,
              expoBusinessNumber: item.expoBusinessNumber
            }),
          });

          const result = await response.json();

          if (response.ok) {
            successCount++;
            savedList.push({
              ...item,
              ucarItmNo: result.ucarItmNo,
              savedAt: new Date().toLocaleString()
            });
            console.log(`✅ 차량 ${item.plateNumber} 등록 성공 (UCAR_ITM_NO: ${result.ucarItmNo})`);
          } else {
            failCount++;
            console.error(`❌ 차량 ${item.plateNumber} 등록 실패:`, result);
          }
        } catch (error) {
          failCount++;
          console.error(`❌ 차량 ${item.plateNumber} 등록 오류:`, error);
        }
      }

      if (savedList.length > 0) {
        setSavedVehicles(savedList);
        setShowSavedVehiclesModal(true);
      }

      alert(`차량 등록 완료!\n성공: ${successCount}건\n실패: ${failCount}건`);

    } catch (error) {
      console.error('❌ 차량 등록 오류:', error);
      alert('차량 등록 중 오류가 발생했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  // 실제 차량 등록 처리
  const processVehicleRegistration = async (checkedItems, forceInsert = false, overwriteId = null) => {
    setIsSaving(true);
    setShowDuplicateModal(false);

    try {
      let successCount = 0;
      let failCount = 0;

      // 체크된 차량만 등록
      for (let idx = 0; idx < checkedItems.length; idx++) {
        const checkedItem = checkedItems[idx];
        const vehicle = checkedItem.item;
        // ⭐ 건별 수출화주 정보 가져오기 (없으면 기본값 사용)
        const vehicleExpo = vehicleExpoCompanies[idx] || {
          companyNo: expoCompanyNo,
          sangho: expoSangho,
          businessNumber: expoBusinessNumber
        };
        console.log('📝 차량 등록 중:', vehicle, '수출화주:', vehicleExpo);

        try {
          // 차량 데이터 준비 (모든 필드 포함)
          const vehicleData = {
            '차명': vehicle.model,
            '차량등록번호': vehicle.plateNumber,
            '차대번호': vehicle.vin,
            '소유자명': ownerName || vehicle.ownerName || '',
            '주민번호': ownerResidentNumber || vehicle.residentNumber || '',
            '주소': ownerAddress || vehicle.address || '',
            '연식': vehicle.modelYear,
            '주행거리': vehicle.mileage,
            '배기량': vehicle.displacement,
            '사용본거지': ownerAddress || vehicle.address || '',
            '총중량': vehicle.totalWeight || vehicle.weight || '',
            '길이': vehicle.length || '',
            '너비': vehicle.width || '',
            '높이': vehicle.height || '',
            'CBM': vehicle.cbm || '',
            '환급요율': refundRate || '',
            '요율': taxRate || '',
            '문서타입': vehicle.documentType
          };

          // ⭐ fileList에서 해당 차량의 파일 경로와 원본 파일명 찾기
          console.log('🔍 [DEBUG] fileList 전체:', fileList);
          console.log('🔍 [DEBUG] vehicle.pdfFilePath:', vehicle.pdfFilePath);
          console.log('🔍 [DEBUG] vehicle.originalFileName:', vehicle.originalFileName);
          console.log('🔍 [DEBUG] vehicle.vehiclePageNumber:', vehicle.vehiclePageNumber);

          let pdfFilePath = vehicle.pdfFilePath || '';
          // ⭐ 원본 파일명: vehicle.originalFileName 우선 사용 (API 응답에서 받은 값)
          let originalFileName = vehicle.originalFileName || '';
          let pageNumber = vehicle.vehiclePageNumber || 1; // ⭐ 페이지 번호

          // ⭐ originalFileName이 없거나 pdfFilePath가 없으면 fileList에서 찾기
          if ((!originalFileName || !pdfFilePath) && fileList && fileList.length > 0) {
            for (const file of fileList) {
              console.log('🔍 [DEBUG] 검사 중인 file:', {
                name: file.name,
                serverOriginalFileName: file.serverOriginalFileName,
                uploadedFilePath: file.uploadedFilePath,
                path: file.path,
                pageResultsCount: file.pageResults?.length
              });

              if (file.pageResults) {
                const matchingPage = file.pageResults.find(p => p.pageNumber === vehicle.vehiclePageNumber);
                console.log('🔍 [DEBUG] matchingPage:', matchingPage);
                if (matchingPage) {
                  // pdfFilePath가 없으면 설정
                  if (!pdfFilePath) {
                    pdfFilePath = file.uploadedFilePath || file.path || '';
                  }
                  // ⭐ 원본 파일명이 없으면 설정: serverOriginalFileName > name > originalName 순서
                  if (!originalFileName) {
                    originalFileName = file.serverOriginalFileName || file.name || file.originalName || '';
                  }
                  console.log(`📎 파일 찾음: ${pdfFilePath}, 원본파일명: ${originalFileName} (페이지 ${vehicle.vehiclePageNumber})`);
                  break;
                }
              }
              // 이미지 파일인 경우
              if (!file.pageResults && (file.uploadedFilePath || file.path)) {
                if (!pdfFilePath) {
                  pdfFilePath = file.uploadedFilePath || file.path || '';
                }
                // ⭐ 원본 파일명이 없으면 설정
                if (!originalFileName) {
                  originalFileName = file.serverOriginalFileName || file.name || file.originalName || '';
                }
                console.log(`📎 이미지 파일 찾음: ${pdfFilePath}, 원본파일명: ${originalFileName}`);
                break;
              }
            }
          }

          // ⭐ 원본 파일명이 없으면 pdfFilePath에서 추출 시도
          if (!originalFileName && pdfFilePath) {
            // 경로에서 파일명 추출 (예: uploads/temp/1234567890.pdf → 1234567890.pdf)
            const pathParts = pdfFilePath.replace(/\\/g, '/').split('/');
            originalFileName = pathParts[pathParts.length - 1] || '';
          }

          console.log('📤 최종 파일 정보:', { pdfFilePath, originalFileName, pageNumber });

          console.log('📤 API 요청:', {
            vehicleData,
            userId,
            groupId: groupCode,
            documentType: vehicle.documentType,
            pdfFilePath: pdfFilePath,
            forceInsert,
            overwriteId
          });

          const response = await fetch(`${apiBaseUrl}/api/vehicle/save`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              vehicleData,
              userId,
              groupId: groupCode,
              writeDate: writeDate, // 작성일 추가
              documentType: vehicle.documentType,
              pdfFilePath: pdfFilePath, // ⭐ 수정: 찾은 파일 경로 사용
              originalFileName: originalFileName, // ⭐ 원본 파일명 추가
              pageNumber: pageNumber, // ⭐ 페이지 번호 추가
              ocrPrompt: '차량등록매핑을 통한 일괄 등록',
              ocrRawResult: vehicle,
              forceInsert, // 중복 무시 옵션
              overwriteId, // 덮어쓸 ID
              // ⭐ 수출화주 정보 추가 (건별 수출화주 사용)
              expoCompanyNo: vehicleExpo.companyNo,
              expoSangho: vehicleExpo.sangho,
              expoBusinessNumber: vehicleExpo.businessNumber,
              // 차주 정보 추가
              ownerName: ownerName || vehicle.ownerName || '',
              ownerResidentNumber: ownerResidentNumber || vehicle.residentNumber || '',
              ownerAddress: ownerAddress || vehicle.address || '',
              // 환급요율, 요율 추가
              refundRate: refundRate || '',
              taxRate: taxRate || ''
            }),
          });

          const result = await response.json();
          console.log('📥 API 응답:', result);

          if (response.ok && result.success) {
            successCount++;
            console.log(`✅ 차량 ${vehicle.plateNumber} 등록 성공`);

            const savedUcarItmNo = result.ucarItmNo || result.carKey;

            // 저장된 차량 정보 저장 (확인용)
            setSavedVehicles(prev => [...prev, {
              ...vehicle,
              ucarItmNo: savedUcarItmNo,
              savedAt: new Date().toISOString()
            }]);

            // 첨부파일 업로드 (저장 성공 후)
            if (attachedFiles.length > 0 && savedUcarItmNo) {
              for (const file of attachedFiles) {
                try {
                  const formData = new FormData();
                  formData.append('file', file);
                  formData.append('carKey', savedUcarItmNo.toString());
                  formData.append('documentType', vehicle.documentType || '기타');
                  formData.append('userId', userId);
                  formData.append('groupId', groupCode);

                  const attachResponse = await fetch(`${apiBaseUrl}/api/attachment/save`, {
                    method: 'POST',
                    body: formData
                  });

                  if (attachResponse.ok) {
                    console.log(`✅ 첨부파일 ${file.name} 업로드 성공`);
                  } else {
                    const attachResult = await attachResponse.json();
                    console.warn(`⚠️ 첨부파일 ${file.name} 업로드 실패:`, attachResult);
                  }
                } catch (attachError) {
                  console.error(`❌ 첨부파일 ${file.name} 업로드 오류:`, attachError);
                }
              }
            }
          } else {
            failCount++;
            console.error(`❌ 차량 ${vehicle.plateNumber} 등록 실패:`, result);
          }
        } catch (error) {
          failCount++;
          console.error(`❌ 차량 ${vehicle.plateNumber} 등록 오류:`, error);
        }
      }

      const message = `차량 등록 완료!\n성공: ${successCount}건\n실패: ${failCount}건${successCount > 0 ? '\n\n저장된 차량을 확인하시겠습니까?' : ''}`;

      if (successCount > 0 && window.confirm(message)) {
        setShowSavedVehiclesModal(true);
      } else {
        alert(message);
      }

      // 상태 초기화
      setExpoSangho('로그인네트웍');
      setExpoBusinessNumber('');
      setOwnerName('');
      setOwnerResidentNumber('');
      setOwnerAddress('');
      setRefundRate('');
      setTaxRate('');
      setAttachedFiles([]);

    } catch (error) {
      console.error('❌ 차량 등록 실패:', error);
      alert(`차량 등록 실패: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // 수출화주 목록 로드 함수
  const loadCompanyList = async (searchText = '') => {
    setIsLoadingCompanies(true);
    try {
      const params = new URLSearchParams({
        companyType: 'A', // 수출업체
        groupCode: groupCode || '0001'
      });
      if (searchText) {
        params.append('searchText', searchText);
      }

      const response = await fetch(`${apiBaseUrl}/api/company/list?${params}`);
      const result = await response.json();

      if (result.success) {
        setCompanyList(result.data || []);
      } else {
        console.error('수출화주 목록 조회 실패:', result);
        setCompanyList([]);
      }
    } catch (error) {
      console.error('수출화주 목록 조회 오류:', error);
      setCompanyList([]);
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  // 수출화주 선택 함수
  const handleSelectCompany = (company) => {
    // 레거시 시스템 필드명 (UCAR_BASE_COMPANY)과 기존 필드명 모두 지원
    setExpoSangho(company.SANGHO || company.companyName || '');
    setExpoBusinessNumber(company.BUSINESS_NUMBER || company.businessNumber || '');
    setExpoCompanyNo(company.COMPANY_NO || company.companyCode || 563);
    setShowCompanySearchModal(false);
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">매칭 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center text-red-600">
          <p className="text-lg font-semibold mb-2">❌ 매칭 오류</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!fileList || fileList.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-gray-500">파일을 업로드하고 처리를 완료하세요.</p>
      </div>
    );
  }

  return (
    <div className="h-full flex p-4 overflow-hidden bg-white">
      {/* 왼쪽: 등록 로직 설명 패널 */}

      {!isVehicleRegistrationPanelCollapsed && (
        <div className="w-80 flex-shrink-0 overflow-y-auto bg-blue-50 rounded-lg border-2 border-blue-200 p-4">
          <h3 className="text-lg font-bold text-blue-900 mb-3 flex items-center gap-2">
            <span>📋</span>
            <span>차량 등록 프로세스</span>
          </h3>

          {/* 프로세스 단계 */}
          <div className="space-y-3">
            {/* 1단계 */}
            <div className="bg-white rounded-lg p-3 border border-blue-300">
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</span>
                <span className="font-bold text-gray-900">차량 선택</span>
              </div>
              <p className="text-xs text-gray-600 ml-8">
                그리드에서 등록할 차량 체크박스 선택
              </p>
            </div>

            {/* 2단계 */}
            <div className="bg-white rounded-lg p-3 border border-blue-300">
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</span>
                <span className="font-bold text-gray-900">등록 정보 입력</span>
              </div>
              <div className="text-xs text-gray-600 ml-8 space-y-1">
                <div>• 그룹코드: <span className="font-mono bg-gray-100 px-1">0001</span></div>
                <div>• 아이디: <span className="font-mono bg-gray-100 px-1">jmkim</span></div>
                <div>• 작성일: <span className="font-mono bg-gray-100 px-1">2024-11-05</span></div>
              </div>
            </div>

            {/* 3단계 */}
            <div className="bg-white rounded-lg p-3 border border-blue-300">
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</span>
                <span className="font-bold text-gray-900">데이터 전송</span>
              </div>
              <div className="text-xs text-gray-600 ml-8 space-y-1">
                <div className="font-mono bg-gray-100 p-2 rounded">
                  POST /api/vehicle/save
                </div>
                <div className="mt-2 font-semibold text-gray-700">전송 필드:</div>
                <div className="space-y-0.5 text-xs">
                  <div>• 차명 (차량명)</div>
                  <div>• 차량등록번호</div>
                  <div>• 차대번호</div>
                  <div>• 연식</div>
                  <div>• 주행거리</div>
                  <div>• 배기량</div>
                  <div>• 소유자명</div>
                  <div>• 주민번호</div>
                  <div>• 사용본거지</div>
                </div>
              </div>
            </div>

            {/* 4단계 */}
            <div className="bg-white rounded-lg p-3 border border-blue-300">
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">4</span>
                <span className="font-bold text-gray-900">DB 저장</span>
              </div>
              <div className="text-xs text-gray-600 ml-8 space-y-1">
                <div className="font-mono bg-gray-100 p-2 rounded">
                  INSERT INTO UCAR_ITEM
                </div>
                <div className="mt-2 font-semibold text-gray-700">저장 컬럼:</div>
                <div className="space-y-0.5 text-xs">
                  <div>• UCAR_ITM_CAR_NM (차명)</div>
                  <div>• UCAR_ITM_CAR_NUM (차량번호)</div>
                  <div>• UCAR_ITM_CAR_CHASSIS (차대번호)</div>
                  <div>• UCAR_ITM_CAR_YEAR (연식)</div>
                  <div>• UCAR_ITM_CAR_MILEAGE (주행거리)</div>
                  <div>• UCAR_ITM_CAR_CC (배기량)</div>
                  <div>• UCAR_ITM_CAR_WEIGHT (중량)</div>
                  <div>• UCAR_ITM_LENGTH (길이)</div>
                  <div>• UCAR_ITM_DEEP (너비)</div>
                  <div>• UCAR_ITM_HEIGHT (높이)</div>
                  <div>• UCAR_ITM_CBM (CBM)</div>
                  <div>• UCAR_ITM_SALE_COST (총금액)</div>
                  <div>• UCAR_ITM_REFUND_COST (세액)</div>
                </div>
              </div>
            </div>

            {/* 5단계 */}
            <div className="bg-white rounded-lg p-3 border border-green-300">
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">5</span>
                <span className="font-bold text-gray-900">등록 완료</span>
              </div>
              <div className="text-xs text-gray-600 ml-8">
                <div>• UCAR_ITM_NO 자동 생성</div>
                <div>• 성공/실패 건수 표시</div>
              </div>
            </div>
          </div>

          {/* 주의사항 */}
          <div className="mt-4 bg-gray-50 rounded-lg p-3 border border-gray-300">
            <div className="flex items-center gap-2 mb-2">
              <span>⚠️</span>
              <span className="font-bold text-yellow-900 text-sm">현재 제약사항</span>
            </div>
            <div className="text-xs text-yellow-800 space-y-1">
              <div>• 중복 체크 없음</div>
              <div>• 순차 처리 (병렬 미지원)</div>
              <div>• 소유자 정보 미저장</div>
            </div>
          </div>

          {/* 상세 문서 링크 */}
          <div className="mt-4 text-center">
            <a
              href="/VEHICLE_REGISTRATION_FLOW.md"
              target="_blank"
              className="text-xs text-blue-600 hover:text-blue-800 underline"
            >
              📄 상세 문서 보기
            </a>
          </div>
        </div>
      )}

      {/* 토글 버튼 (새로 추가됨) */}
      <div className="relative w-4 flex-shrink-0 flex items-center justify-center z-10">
        <div
          className="absolute -right-2 top-1/2 -translate-y-1/2 z-10 cursor-pointer"
          onClick={() => setIsVehicleRegistrationPanelCollapsed(!isVehicleRegistrationPanelCollapsed)}
          title={isVehicleRegistrationPanelCollapsed ? "등록 프로세스 펼치기" : "등록 프로세스 접기"}
        >
          <div className="w-4 h-12 bg-white border border-gray-300 rounded-r-md flex items-center justify-center shadow-sm hover:bg-gray-50 text-gray-500 hover:text-blue-600">
            {isVehicleRegistrationPanelCollapsed ? <ChevronRight size={14} strokeWidth={2} /> : <ChevronLeft size={14} strokeWidth={2} />}
          </div>
        </div>
      </div>

      {/* 오른쪽: 기존 그리드 영역 */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* 요약 정보 */}
        {summary && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-300">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold text-gray-900">매칭 요약</h3>
              <button
                onClick={handleRegisterVehiclesClick}
                disabled={isSaving || !vehicleData || vehicleData.length === 0}
                className="px-6 py-2 rounded-lg font-bold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-gray-700 hover:bg-gray-800 text-white"
              >
                {isSaving ? '등록 중...' : '↑ 선택 차량 등록'}
              </button>
            </div>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-600">전체 차량 문서:</span>
                <span className="ml-2 font-semibold text-gray-900">{summary.totalVehicles}건</span>
              </div>
              <div>
                <span className="text-gray-600">신분증/사업자:</span>
                <span className="ml-2 font-semibold text-gray-900">{summary.totalIdentities}건</span>
              </div>
              <div>
                <span className="text-gray-600">매칭 성공:</span>
                <span className="ml-2 font-semibold text-gray-900">{summary.matchedCount}건</span>
              </div>
              <div>
                <span className="text-gray-600">미매칭:</span>
                <span className="ml-2 font-semibold text-gray-900">{summary.unmatchedCount}건</span>
              </div>
            </div>
          </div>
        )}

        {/* 차량 문서 (상단) */}
        {vehicleData.length > 0 && (
          <div className="flex-1 flex flex-col mb-3 overflow-hidden">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-base font-bold text-gray-900">
                차량 문서 ({vehicleData.length}건)
                <span className="ml-2 text-xs font-normal text-gray-600">
                  · 자동차등록증, 등록원부, 말소등록증, 경락확인서, 매매계약서
                </span>
              </h3>
              <button
                onClick={handleExportVehicles}
                className="px-3 py-1 text-sm bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors"
              >
                ↓ 엑셀 다운로드
              </button>
            </div>
            <div
              id="vehicle-grid"
              ref={matchedGridRef}
              style={{ width: '100%', height: '100%' }}
            ></div>
          </div>
        )}

        {/* 부가 서류 (하단) */}
        {supportData.length > 0 && (
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-base font-bold text-gray-900">
                부가 서류 ({supportData.length}건)
                <span className="ml-2 text-xs font-normal text-gray-600">
                  · 신분증, 사업자등록증, 매매계약서, 세금계산서
                </span>
              </h3>
              <button
                onClick={handleExportSupports}
                className="px-3 py-1 text-sm bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors"
              >
                ↓ 엑셀 다운로드
              </button>
            </div>
            <div
              id="support-grid"
              ref={unmatchedGridRef}
              style={{ width: '100%', height: '100%' }}
            ></div>
          </div>
        )}

        {vehicleData.length === 0 && supportData.length === 0 && (
          <div className="h-full flex items-center justify-center">
            <p className="text-gray-500">매칭 가능한 문서가 없습니다.</p>
          </div>
        )}
      </div>

      {/* 차량 등록 모달 - 그리드 기반 컴팩트 UI */}
      {showRegistrationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg shadow-xl w-[95vw] max-w-[1400px] h-[90vh] flex flex-col">
            {/* 헤더 */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                🚗 차량 일괄 등록
                <span className="text-sm font-normal text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                  {window.AUIGrid && window.AUIGrid.isCreated('#vehicle-grid') ? window.AUIGrid.getCheckedRowItems('#vehicle-grid').length : 0}건
                </span>
              </h3>
              <button
                onClick={() => {
                  setShowRegistrationModal(false);
                  setExpoSangho('로그인네트웍');
                  setExpoBusinessNumber('');
                  setOwnerName('');
                  setOwnerResidentNumber('');
                  setOwnerAddress('');
                  setRefundRate('');
                  setTaxRate('');
                  setAttachedFiles([]);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            {/* 컨텐츠 영역 */}
            <div className="flex-1 flex overflow-hidden">
              {/* 왼쪽: 공통 설정 (컴팩트) */}
              <div className="w-[280px] border-r border-gray-200 p-3 overflow-y-auto bg-gray-50">
                {/* 그룹코드/아이디 (맨 위) */}
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">그룹코드 *</label>
                    <input
                      type="text"
                      value={groupCode}
                      onChange={(e) => setGroupCode(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-blue-50"
                      placeholder="0001"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">아이디 *</label>
                    <input
                      type="text"
                      value={userId}
                      onChange={(e) => setUserId(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-blue-50"
                      placeholder="jmkim"
                    />
                  </div>
                </div>

                {/* 작성일 */}
                <div className="mb-3">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">작성일 *</label>
                  <input
                    type="date"
                    value={writeDate}
                    onChange={(e) => setWriteDate(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-blue-50"
                  />
                </div>

                <hr className="my-3 border-gray-300" />

                {/* 수출화주 (전체 적용) */}
                <div className="mb-3">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">수출화주 *</label>
                  <div className="flex gap-1">
                    <input
                      type="text"
                      value={expoSangho}
                      className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50"
                      placeholder="수출화주 선택"
                      readOnly
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setShowCompanySearchModal(true);
                        setCompanySearchText('');
                        loadCompanyList('');
                      }}
                      className="px-2 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs"
                      title="수출화주 검색"
                    >
                      🔍
                    </button>
                  </div>
                  {expoBusinessNumber && (
                    <p className="text-xs text-gray-500 mt-1">사업자번호: {expoBusinessNumber}</p>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      // 전체 차량에 현재 수출화주 적용
                      if (window.AUIGrid && window.AUIGrid.isCreated('#registration-grid')) {
                        const allData = window.AUIGrid.getGridData('#registration-grid');
                        const updatedData = allData.map(item => {
                          const newExpoSangho = expoSangho || '';
                          const plateNum = item.plateNumber || '';
                          let newDisplayFileName = '';
                          if (newExpoSangho && plateNum) {
                            const expoName = newExpoSangho.substring(0, 10);
                            newDisplayFileName = `${expoName}_${plateNum}.pdf`;
                          } else {
                            const baseName = (item.originalFileName || '').replace(/\.[^/.]+$/, '');
                            newDisplayFileName = `${baseName}_${item.pageNumber || 1}.pdf`;
                          }
                          return {
                            ...item,
                            expoCompanyNo: expoCompanyNo,
                            expoSangho: newExpoSangho,
                            expoBusinessNumber: expoBusinessNumber,
                            displayFileName: newDisplayFileName
                          };
                        });
                        window.AUIGrid.setGridData('#registration-grid', updatedData);
                        setRegistrationGridData(updatedData);
                      }
                    }}
                    className="w-full mt-2 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded text-xs font-medium"
                  >
                    ✓ 전체 차량에 적용
                  </button>
                </div>

                <hr className="my-3 border-gray-300" />

                {/* 환급/요율 (가로 배치) */}
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">환급요율(%)</label>
                    <input
                      type="number"
                      value={refundRate}
                      onChange={(e) => setRefundRate(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="88"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">요율(%)</label>
                    <input
                      type="number"
                      value={taxRate}
                      onChange={(e) => setTaxRate(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="10"
                    />
                  </div>
                </div>

                <hr className="my-3 border-gray-300" />

                {/* 추가 첨부파일 */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">추가 첨부파일</label>
                  <input
                    type="file"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      setAttachedFiles(prev => [...prev, ...files]);
                    }}
                    className="w-full text-xs border border-gray-300 rounded p-1"
                  />
                  {attachedFiles.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {attachedFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between text-xs p-1 bg-white rounded border">
                          <span className="truncate">{file.name}</span>
                          <button
                            onClick={() => setAttachedFiles(prev => prev.filter((_, i) => i !== index))}
                            className="text-red-500 hover:text-red-700 ml-1"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* 오른쪽: 차량 그리드 (AUIGrid) */}
              <div className="flex-1 flex flex-col p-3 overflow-hidden">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-bold text-gray-700">
                    📋 등록 차량 목록
                    <span className="text-xs font-normal text-gray-500 ml-2">
                      (수출화주 클릭 시 변경 가능)
                    </span>
                  </h4>
                </div>

                {/* AUIGrid 컨테이너 */}
                <div
                  id="registration-grid"
                  ref={registrationGridRef}
                  className="flex-1 border border-gray-300 rounded"
                  style={{ minHeight: '300px' }}
                />
              </div>
            </div>

            {/* 푸터 */}
            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <button
                onClick={() => {
                  setShowRegistrationModal(false);
                  setExpoSangho('로그인네트웍');
                  setExpoBusinessNumber('');
                  setOwnerName('');
                  setOwnerResidentNumber('');
                  setOwnerAddress('');
                  setRefundRate('');
                  setTaxRate('');
                  setAttachedFiles([]);
                }}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded font-medium hover:bg-gray-300 transition-colors text-sm"
              >
                취소
              </button>
              <button
                onClick={handleRegisterVehicles}
                disabled={isSaving}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    등록 중...
                  </>
                ) : (
                  <>
                    ✓ 일괄 등록
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 중복 확인 모달 (그리드 형식) */}
      {showDuplicateModal && allDuplicateResults && allDuplicateResults.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg p-6 w-[1200px] max-h-[90vh] flex flex-col shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-red-600 flex items-center gap-2">
                <span>⚠️</span>
                <span>중복 차량 발견 ({allDuplicateResults.length}건)</span>
              </h3>
              <button
                onClick={() => {
                  setShowDuplicateModal(false);
                  setAllDuplicateResults([]);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            {/* 상단 그리드: 업로드한 차량 리스트 */}
            <div className="mb-4">
              <h4 className="text-lg font-bold mb-2 text-blue-900">📤 등록하려는 차량 ({allDuplicateResults.length}건)</h4>
              <div
                id="upload-vehicle-grid"
                style={{ width: '100%', height: '200px' }}
              ></div>
            </div>

            {/* 하단 그리드: 중복건 목록 */}
            <div className="flex-1 overflow-hidden">
              <h4 className="text-lg font-bold mb-2 text-yellow-900">
                📋 중복 차량 목록 (총 {allDuplicateResults.reduce((sum, result) => sum + result.duplicates.length, 0)}건)
              </h4>
              <div className="overflow-auto" style={{ maxHeight: '400px' }}>
                <div
                  id="duplicate-vehicle-grid"
                  style={{ width: '100%', height: '400px' }}
                ></div>
              </div>
            </div>

            {/* 안내 메시지 */}
            <div className="mt-4 p-3 bg-gray-50 rounded border border-gray-300 text-sm">
              <div className="font-semibold text-gray-900 mb-2">📌 중복 처리 방법</div>
              <div className="space-y-1 text-gray-700">
                <div>• <strong>오버라이트 저장</strong>: 각 차량의 첫 번째 중복 차량으로 덮어씁니다</div>
                <div>• <strong>신규 추가</strong>: 중복을 무시하고 새로운 레코드로 추가합니다</div>
                <div>• <strong>취소</strong>: 등록을 취소합니다</div>
              </div>
            </div>

            {/* 버튼 */}
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => {
                  setShowDuplicateModal(false);
                  setAllDuplicateResults([]);
                  setDuplicateInfo(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
              >
                취소
              </button>
              <button
                onClick={() => {
                  // 모든 차량을 오버라이트 저장 (각 차량의 첫 번째 중복 ID 사용)
                  processVehicleRegistrationForAllDuplicates(pendingVehicles, false);
                }}
                className="flex-1 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-medium transition-colors"
              >
                🔄 전체 오버라이트 저장
              </button>
              <button
                onClick={() => {
                  // 신규 추가 (forceInsert = true)
                  processVehicleRegistration(pendingVehicles, true, null);
                }}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                ➕ 전체 신규 추가
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 기존 테이블 모달 (하위 호환성) */}
      {showDuplicateModal && duplicateInfo && allDuplicateResults.length === 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg p-6 w-[900px] max-h-[90vh] overflow-y-auto shadow-xl">
            <h3 className="text-xl font-bold mb-4 text-red-600 flex items-center gap-2">
              <span>⚠️</span>
              <span>중복 차량 발견</span>
            </h3>

            {/* 비교 테이블 */}
            <div className="mb-4 overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-3 py-2 text-left font-bold w-32">필드명</th>
                    <th className="border border-blue-300 bg-blue-50 px-3 py-2 text-left font-bold">
                      📝 등록하려는 차량 (신규)
                    </th>
                    {duplicateInfo.duplicates.map((dup, index) => (
                      <th key={index} className="border border-gray-300 bg-gray-50 px-3 py-2 text-left font-bold">
                        📋 기존 등록 차량 #{index + 1}
                        <div className="text-xs font-normal text-gray-600 mt-1">
                          등록자: {dup.regId}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Key */}
                  <tr>
                    <td className="border border-gray-300 px-3 py-2 font-semibold bg-gray-50">Key</td>
                    <td className="border border-blue-300 bg-blue-50 px-3 py-2 text-gray-500">
                      (신규 등록)
                    </td>
                    {duplicateInfo.duplicates.map((dup, index) => (
                      <td key={index} className="border border-gray-300 bg-gray-50 px-3 py-2 font-mono">
                        {dup.ucarItmNo || '-'}
                      </td>
                    ))}
                  </tr>

                  {/* 수출화주 */}
                  <tr>
                    <td className="border border-gray-300 px-3 py-2 font-semibold bg-gray-50">수출화주</td>
                    <td className="border border-blue-300 bg-blue-50 px-3 py-2">
                      로그인네트웍
                    </td>
                    {duplicateInfo.duplicates.map((dup, index) => (
                      <td key={index} className="border border-gray-300 bg-gray-50 px-3 py-2">
                        로그인네트웍
                      </td>
                    ))}
                  </tr>

                  {/* 차량번호 */}
                  <tr>
                    <td className="border border-gray-300 px-3 py-2 font-semibold bg-gray-50">차량번호</td>
                    <td className="border border-blue-300 bg-blue-50 px-3 py-2 font-semibold">
                      {duplicateInfo.vehicle.plateNumber || '-'}
                    </td>
                    {duplicateInfo.duplicates.map((dup, index) => (
                      <td key={index} className={`border border-gray-300 bg-gray-50 px-3 py-2 font-semibold ${dup.plateNumber === duplicateInfo.vehicle.plateNumber ? 'text-red-600 bg-red-50' : ''
                        }`}>
                        {dup.plateNumber || '-'}
                      </td>
                    ))}
                  </tr>

                  {/* 차대번호 */}
                  <tr>
                    <td className="border border-gray-300 px-3 py-2 font-semibold bg-gray-50">차대번호</td>
                    <td className="border border-blue-300 bg-blue-50 px-3 py-2 text-xs">
                      {duplicateInfo.vehicle.vin || '-'}
                    </td>
                    {duplicateInfo.duplicates.map((dup, index) => (
                      <td key={index} className={`border border-gray-300 bg-gray-50 px-3 py-2 text-xs ${dup.vin === duplicateInfo.vehicle.vin ? 'text-red-600 bg-red-50 font-semibold' : ''
                        }`}>
                        {dup.vin || '-'}
                      </td>
                    ))}
                  </tr>

                  {/* 차명 */}
                  <tr>
                    <td className="border border-gray-300 px-3 py-2 font-semibold bg-gray-50">차명</td>
                    <td className="border border-blue-300 bg-blue-50 px-3 py-2">
                      {duplicateInfo.vehicle.model || '-'}
                    </td>
                    {duplicateInfo.duplicates.map((dup, index) => (
                      <td key={index} className="border border-gray-300 bg-gray-50 px-3 py-2">
                        {dup.carName || '-'}
                      </td>
                    ))}
                  </tr>

                  {/* 연식 */}
                  <tr>
                    <td className="border border-gray-300 px-3 py-2 font-semibold bg-gray-50">연식</td>
                    <td className="border border-blue-300 bg-blue-50 px-3 py-2">
                      {duplicateInfo.vehicle.modelYear || '-'}
                    </td>
                    {duplicateInfo.duplicates.map((dup, index) => (
                      <td key={index} className="border border-gray-300 bg-gray-50 px-3 py-2">
                        {dup.year || '-'}
                      </td>
                    ))}
                  </tr>

                  {/* 소유자명 */}
                  <tr>
                    <td className="border border-gray-300 px-3 py-2 font-semibold bg-gray-50">소유자명</td>
                    <td className="border border-blue-300 bg-blue-50 px-3 py-2">
                      {duplicateInfo.vehicle.ownerName || '-'}
                    </td>
                    {duplicateInfo.duplicates.map((dup, index) => (
                      <td key={index} className="border border-gray-300 bg-gray-50 px-3 py-2">
                        -
                      </td>
                    ))}
                  </tr>

                  {/* 주행거리 */}
                  <tr>
                    <td className="border border-gray-300 px-3 py-2 font-semibold bg-gray-50">주행거리</td>
                    <td className="border border-blue-300 bg-blue-50 px-3 py-2 text-right">
                      {duplicateInfo.vehicle.mileage ? parseInt(String(duplicateInfo.vehicle.mileage).replace(/[^0-9]/g, '')).toLocaleString() : '-'}
                    </td>
                    {duplicateInfo.duplicates.map((dup, index) => (
                      <td key={index} className="border border-gray-300 bg-gray-50 px-3 py-2 text-right">
                        {dup.mileage ? dup.mileage.toLocaleString() : '-'}
                      </td>
                    ))}
                  </tr>

                  {/* 배기량 */}
                  <tr>
                    <td className="border border-gray-300 px-3 py-2 font-semibold bg-gray-50">배기량</td>
                    <td className="border border-blue-300 bg-blue-50 px-3 py-2 text-right">
                      {duplicateInfo.vehicle.displacement ? parseInt(String(duplicateInfo.vehicle.displacement).replace(/[^0-9]/g, '')).toLocaleString() : '-'}
                    </td>
                    {duplicateInfo.duplicates.map((dup, index) => (
                      <td key={index} className="border border-gray-300 bg-gray-50 px-3 py-2 text-right">
                        -
                      </td>
                    ))}
                  </tr>

                  {/* 총중량 */}
                  <tr>
                    <td className="border border-gray-300 px-3 py-2 font-semibold bg-gray-50">총중량</td>
                    <td className="border border-blue-300 bg-blue-50 px-3 py-2 text-right">
                      {duplicateInfo.vehicle.weight ? parseFloat(String(duplicateInfo.vehicle.weight).replace(/[^0-9.]/g, '')).toLocaleString() : '-'}
                    </td>
                    {duplicateInfo.duplicates.map((dup, index) => (
                      <td key={index} className="border border-gray-300 bg-gray-50 px-3 py-2 text-right">
                        -
                      </td>
                    ))}
                  </tr>

                  {/* 길이 */}
                  <tr>
                    <td className="border border-gray-300 px-3 py-2 font-semibold bg-gray-50">길이</td>
                    <td className="border border-blue-300 bg-blue-50 px-3 py-2 text-right">
                      {duplicateInfo.vehicle.length || '-'}
                    </td>
                    {duplicateInfo.duplicates.map((dup, index) => (
                      <td key={index} className="border border-gray-300 bg-gray-50 px-3 py-2 text-right">
                        -
                      </td>
                    ))}
                  </tr>

                  {/* 너비 */}
                  <tr>
                    <td className="border border-gray-300 px-3 py-2 font-semibold bg-gray-50">너비</td>
                    <td className="border border-blue-300 bg-blue-50 px-3 py-2 text-right">
                      {duplicateInfo.vehicle.width || '-'}
                    </td>
                    {duplicateInfo.duplicates.map((dup, index) => (
                      <td key={index} className="border border-gray-300 bg-gray-50 px-3 py-2 text-right">
                        -
                      </td>
                    ))}
                  </tr>

                  {/* 높이 */}
                  <tr>
                    <td className="border border-gray-300 px-3 py-2 font-semibold bg-gray-50">높이</td>
                    <td className="border border-blue-300 bg-blue-50 px-3 py-2 text-right">
                      {duplicateInfo.vehicle.height || '-'}
                    </td>
                    {duplicateInfo.duplicates.map((dup, index) => (
                      <td key={index} className="border border-gray-300 bg-gray-50 px-3 py-2 text-right">
                        -
                      </td>
                    ))}
                  </tr>

                  {/* CBM */}
                  <tr>
                    <td className="border border-gray-300 px-3 py-2 font-semibold bg-gray-50">CBM</td>
                    <td className="border border-blue-300 bg-blue-50 px-3 py-2 text-right">
                      {duplicateInfo.vehicle.cbm || '-'}
                    </td>
                    {duplicateInfo.duplicates.map((dup, index) => (
                      <td key={index} className="border border-gray-300 bg-gray-50 px-3 py-2 text-right">
                        -
                      </td>
                    ))}
                  </tr>

                  {/* 총금액 */}
                  <tr>
                    <td className="border border-gray-300 px-3 py-2 font-semibold bg-gray-50">총금액</td>
                    <td className="border border-blue-300 bg-blue-50 px-3 py-2 text-right">
                      {duplicateInfo.vehicle.totalAmount ? parseFloat(String(duplicateInfo.vehicle.totalAmount).replace(/[^0-9.]/g, '')).toLocaleString() : '-'}
                    </td>
                    {duplicateInfo.duplicates.map((dup, index) => (
                      <td key={index} className="border border-gray-300 bg-gray-50 px-3 py-2 text-right">
                        -
                      </td>
                    ))}
                  </tr>

                  {/* 세액 */}
                  <tr>
                    <td className="border border-gray-300 px-3 py-2 font-semibold bg-gray-50">세액</td>
                    <td className="border border-blue-300 bg-blue-50 px-3 py-2 text-right">
                      {duplicateInfo.vehicle.tax ? parseFloat(String(duplicateInfo.vehicle.tax).replace(/[^0-9.]/g, '')).toLocaleString() : '-'}
                    </td>
                    {duplicateInfo.duplicates.map((dup, index) => (
                      <td key={index} className="border border-gray-300 bg-gray-50 px-3 py-2 text-right">
                        -
                      </td>
                    ))}
                  </tr>

                  {/* 사용본거지 */}
                  <tr>
                    <td className="border border-gray-300 px-3 py-2 font-semibold bg-gray-50">사용본거지</td>
                    <td className="border border-blue-300 bg-blue-50 px-3 py-2 text-xs">
                      {duplicateInfo.vehicle.address || '-'}
                    </td>
                    {duplicateInfo.duplicates.map((dup, index) => (
                      <td key={index} className="border border-gray-300 bg-gray-50 px-3 py-2 text-xs">
                        -
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>

            {/* 안내 메시지 */}
            <div className="mb-4 p-3 bg-gray-50 rounded border border-gray-300 text-sm">
              <div className="font-semibold text-gray-900 mb-2">📌 중복 처리 방법</div>
              <div className="space-y-1 text-gray-700">
                <div>• <strong>오버라이트 저장</strong>: 기존 데이터를 새 데이터로 덮어씁니다</div>
                <div>• <strong>신규 추가</strong>: 중복을 무시하고 새로운 레코드로 추가합니다</div>
                <div>• <strong>취소</strong>: 등록을 취소합니다</div>
              </div>
            </div>

            {/* 버튼 */}
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowDuplicateModal(false);
                  setDuplicateInfo(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
              >
                취소
              </button>
              <button
                onClick={() => {
                  // 오버라이트 저장 (첫 번째 중복 차량 ID 사용)
                  const overwriteId = duplicateInfo.duplicates[0].ucarItmNo;
                  processVehicleRegistration(pendingVehicles, false, overwriteId);
                }}
                className="flex-1 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-medium transition-colors"
              >
                🔄 오버라이트 저장
              </button>
              <button
                onClick={() => {
                  // 신규 추가 (forceInsert = true)
                  processVehicleRegistration(pendingVehicles, true, null);
                }}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                ➕ 신규 추가
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 수출화주 검색 팝업 */}
      {showCompanySearchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg p-6 w-[800px] max-h-[90vh] flex flex-col shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">수출화주 검색</h3>
              <button
                onClick={() => {
                  setShowCompanySearchModal(false);
                  setCompanySearchText('');
                  setCompanyList([]);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            {/* 검색 입력 */}
            <div className="mb-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={companySearchText}
                  onChange={(e) => setCompanySearchText(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      loadCompanyList(companySearchText);
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="상호, 사업자번호로 검색..."
                />
                <button
                  onClick={() => loadCompanyList(companySearchText)}
                  disabled={isLoadingCompanies}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {isLoadingCompanies ? '검색 중...' : '검색'}
                </button>
              </div>
            </div>

            {/* 수출화주 목록 */}
            <div className="flex-1 overflow-auto border border-gray-300 rounded-lg">
              {isLoadingCompanies ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                  <p className="text-gray-600">검색 중...</p>
                </div>
              ) : companyList.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  검색 결과가 없습니다.
                </div>
              ) : (
                <table className="w-full border-collapse text-sm">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="border border-gray-300 px-3 py-2 text-left font-bold">상호</th>
                      <th className="border border-gray-300 px-3 py-2 text-left font-bold">사업자번호</th>
                      <th className="border border-gray-300 px-3 py-2 text-left font-bold">대표자</th>
                      <th className="border border-gray-300 px-3 py-2 text-left font-bold">주소</th>
                      <th className="border border-gray-300 px-3 py-2 text-center font-bold w-20">선택</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companyList.map((company, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="border border-gray-300 px-3 py-2">{company.SANGHO || company.companyName || '-'}</td>
                        <td className="border border-gray-300 px-3 py-2 font-mono text-xs">{company.BUSINESS_NUMBER || company.businessNumber || '-'}</td>
                        <td className="border border-gray-300 px-3 py-2">{company.CEO_NAME || company.ceoName || '-'}</td>
                        <td className="border border-gray-300 px-3 py-2 text-xs">{company.ADDRESS || company.address || '-'}</td>
                        <td className="border border-gray-300 px-3 py-2 text-center">
                          <button
                            onClick={() => handleSelectCompany(company)}
                            className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs transition-colors"
                          >
                            선택
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="mt-4 flex gap-2">
              <button
                onClick={() => {
                  setShowCompanySearchModal(false);
                  setCompanySearchText('');
                  setCompanyList([]);
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 수출화주 검색 팝업 (건별) */}
      {showVehicleCompanySearch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
          <div className="bg-white rounded-lg p-4 w-[600px] max-h-[70vh] flex flex-col shadow-xl">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-gray-900">
                🏢 수출화주 선택
                {typeof vehicleCompanySearchIdx === 'number' && (
                  <span className="ml-2 text-sm font-normal text-blue-600">
                    ({vehicleCompanySearchIdx + 1}번 차량)
                  </span>
                )}
              </h3>
              <button
                onClick={() => {
                  setShowVehicleCompanySearch(false);
                  setVehicleCompanySearchIdx(null);
                }}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ×
              </button>
            </div>

            {/* 검색 입력 */}
            <div className="mb-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={companySearchText}
                  onChange={(e) => {
                    setCompanySearchText(e.target.value);
                    // 자동완성: 입력 시 자동 검색
                    if (e.target.value.length >= 1) {
                      loadCompanyList(e.target.value);
                    }
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      loadCompanyList(companySearchText);
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="상호, 사업자번호로 검색 (입력 시 자동완성)"
                  autoFocus
                />
                <button
                  onClick={() => loadCompanyList(companySearchText)}
                  disabled={isLoadingCompanies}
                  className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors disabled:opacity-50 text-sm"
                >
                  {isLoadingCompanies ? '...' : '검색'}
                </button>
              </div>
            </div>

            {/* 수출화주 목록 */}
            <div className="flex-1 overflow-auto border border-gray-300 rounded" style={{ maxHeight: '400px' }}>
              {isLoadingCompanies ? (
                <div className="p-6 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto mb-2"></div>
                  <p className="text-gray-600 text-sm">검색 중...</p>
                </div>
              ) : companyList.length === 0 ? (
                <div className="p-6 text-center text-gray-500 text-sm">
                  {companySearchText ? '검색 결과가 없습니다.' : '상호 또는 사업자번호를 입력하세요.'}
                </div>
              ) : (
                <table className="w-full border-collapse text-xs">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="border-b px-3 py-2 text-left font-semibold">상호</th>
                      <th className="border-b px-3 py-2 text-left font-semibold">사업자번호</th>
                      <th className="border-b px-3 py-2 text-left font-semibold">대표자</th>
                      <th className="border-b px-3 py-2 text-center font-semibold w-16">선택</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companyList.map((company, index) => (
                      <tr key={index} className="hover:bg-blue-50 cursor-pointer" onClick={() => {
                        // 수출화주 선택 처리
                        console.log('🏢 수출화주 선택:', company.SANGHO, 'vehicleCompanySearchIdx:', vehicleCompanySearchIdx);

                        // ⭐ React state 기반으로 데이터 업데이트 (그리드 재생성 없이)
                        let updatedData = [...registrationGridData];

                        if (vehicleCompanySearchIdx === null) {
                          // 전체 적용
                          console.log('🔄 전체 적용');
                          updatedData = updatedData.map(item => {
                            const newExpoSangho = company.SANGHO || '';
                            const plateNum = item.plateNumber || '';
                            let newDisplayFileName = '';
                            if (newExpoSangho && plateNum) {
                              const expoName = newExpoSangho.substring(0, 10);
                              newDisplayFileName = `${expoName}_${plateNum}.pdf`;
                            } else {
                              const baseName = (item.originalFileName || '').replace(/\.[^/.]+$/, '');
                              newDisplayFileName = `${baseName}_${item.pageNumber || 1}.pdf`;
                            }
                            return {
                              ...item,
                              expoCompanyNo: company.COMPANY_NO,
                              expoSangho: newExpoSangho,
                              expoBusinessNumber: company.BUSINESS_NUMBER || '',
                              displayFileName: newDisplayFileName
                            };
                          });
                        } else if (vehicleCompanySearchIdx === 'selected') {
                          // 선택된 행에만 적용
                          if (window.AUIGrid && window.AUIGrid.isCreated('#registration-grid')) {
                            const checkedRowIndexes = window.AUIGrid.getCheckedRowItems('#registration-grid')
                              .map(row => row.rowIndex);
                            console.log('🔄 선택된 행만 적용:', checkedRowIndexes);
                            updatedData = updatedData.map((item, idx) => {
                              if (checkedRowIndexes.includes(idx)) {
                                const newExpoSangho = company.SANGHO || '';
                                const plateNum = item.plateNumber || '';
                                let newDisplayFileName = '';
                                if (newExpoSangho && plateNum) {
                                  const expoName = newExpoSangho.substring(0, 10);
                                  newDisplayFileName = `${expoName}_${plateNum}.pdf`;
                                } else {
                                  const baseName = (item.originalFileName || '').replace(/\.[^/.]+$/, '');
                                  newDisplayFileName = `${baseName}_${item.pageNumber || 1}.pdf`;
                                }
                                return {
                                  ...item,
                                  expoCompanyNo: company.COMPANY_NO,
                                  expoSangho: newExpoSangho,
                                  expoBusinessNumber: company.BUSINESS_NUMBER || '',
                                  displayFileName: newDisplayFileName
                                };
                              }
                              return item;
                            });
                          }
                        } else {
                          // 특정 행에만 적용 (rowIndex 사용)
                          const targetIdx = parseInt(vehicleCompanySearchIdx, 10);
                          console.log('🔄 특정 행만 적용 (rowIndex):', targetIdx);
                          updatedData = updatedData.map((item, idx) => {
                            if (idx === targetIdx) {
                              const newExpoSangho = company.SANGHO || '';
                              const plateNum = item.plateNumber || '';
                              // ⭐ 표시용 파일명도 함께 업데이트
                              let newDisplayFileName = '';
                              if (newExpoSangho && plateNum) {
                                const expoName = newExpoSangho.substring(0, 10);
                                newDisplayFileName = `${expoName}_${plateNum}.pdf`;
                              } else {
                                const baseName = (item.originalFileName || '').replace(/\.[^/.]+$/, '');
                                newDisplayFileName = `${baseName}_${item.pageNumber || 1}.pdf`;
                              }
                              console.log('  ✅ 매칭! idx=', idx, '수출화주 변경:', item.expoSangho, '→', newExpoSangho, '파일명:', newDisplayFileName);
                              return {
                                ...item,
                                expoCompanyNo: company.COMPANY_NO,
                                expoSangho: newExpoSangho,
                                expoBusinessNumber: company.BUSINESS_NUMBER || '',
                                displayFileName: newDisplayFileName // ⭐ 표시용 파일명 업데이트
                              };
                            }
                            return item;
                          });
                        }

                        console.log('📊 업데이트된 데이터:', updatedData);
                        console.log('📊 각 행의 expoSangho:', updatedData.map(d => d.expoSangho));

                        // ⭐ React state 업데이트 → useEffect에서 그리드 재생성
                        setRegistrationGridData(updatedData);
                        setShowVehicleCompanySearch(false);
                        setVehicleCompanySearchIdx(null);
                      }}>
                        <td className="border-b px-3 py-2 font-medium">{company.SANGHO || company.companyName || '-'}</td>
                        <td className="border-b px-3 py-2 font-mono">{company.BUSINESS_NUMBER || company.businessNumber || '-'}</td>
                        <td className="border-b px-3 py-2">{company.CEO_NAME || company.ceoName || '-'}</td>
                        <td className="border-b px-3 py-2 text-center">
                          <span className="text-blue-500">선택</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="mt-3 flex justify-end">
              <button
                onClick={() => {
                  setShowVehicleCompanySearch(false);
                  setVehicleCompanySearchIdx(null);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded font-medium hover:bg-gray-300 transition-colors text-sm"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 저장된 차량 확인 모달 */}
      {showSavedVehiclesModal && savedVehicles.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg p-6 w-[1000px] max-h-[90vh] flex flex-col shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-green-600 flex items-center gap-2">
                <span>✅</span>
                <span>저장된 차량 목록 ({savedVehicles.length}건)</span>
              </h3>
              <button
                onClick={() => {
                  setShowSavedVehiclesModal(false);
                  setSavedVehicles([]);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-auto mb-4">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-100 sticky top-0">
                    <th className="border border-gray-300 px-3 py-2 text-left font-bold">번호</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-bold">차량번호</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-bold">차대번호</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-bold">차명</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-bold">연식</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-bold">차주명</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-bold">수출화주</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-bold">UCAR_ITM_NO</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-bold">저장시간</th>
                  </tr>
                </thead>
                <tbody>
                  {savedVehicles.map((vehicle, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-3 py-2 text-center">{index + 1}</td>
                      <td className="border border-gray-300 px-3 py-2">{vehicle.plateNumber || '-'}</td>
                      <td className="border border-gray-300 px-3 py-2 text-xs">{vehicle.vin || '-'}</td>
                      <td className="border border-gray-300 px-3 py-2">{vehicle.model || '-'}</td>
                      <td className="border border-gray-300 px-3 py-2">{vehicle.modelYear || '-'}</td>
                      <td className="border border-gray-300 px-3 py-2">{ownerName || vehicle.ownerName || '-'}</td>
                      <td className="border border-gray-300 px-3 py-2">{expoSangho || '-'}</td>
                      <td className="border border-gray-300 px-3 py-2 font-mono">{vehicle.ucarItmNo || '-'}</td>
                      <td className="border border-gray-300 px-3 py-2 text-xs">
                        {vehicle.savedAt ? new Date(vehicle.savedAt).toLocaleString('ko-KR') : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowSavedVehiclesModal(false);
                  setSavedVehicles([]);
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
              >
                닫기
              </button>
              <button
                onClick={() => {
                  window.open(`https://ucar.tlogin.net/item/list.do?group_id=${groupCode}`, '_blank');
                }}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                ucar.tlogin.net에서 확인
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleMatchingPanel;

